import type { GameState, ShotResult } from '../shared/types';
import resultAssets from '../shared/battle-result-assets.json';
import { sourceFrameBlend } from './source-frame-blend';

export type BattleResultOutcome = 'win' | 'loss' | 'draw';
export interface BattleResultFrame {
  key: string;
  outcome: BattleResultOutcome;
  stage: 'waiting' | 'movie' | 'settlement';
  startedAt: number | null;
  elapsed: number;
  /** Native source UI coordinates, before a containing stage's border offset. */
  position: { x: number; y: number };
  movie: null | {
    path: string; frameIndex: number; sourceFrame: number;
    crop: { x: number; y: number; width: number; height: number };
    nextCrop: { x: number; y: number; width: number; height: number };
    mix: number;
    width: number; height: number; anchor: { x: number; y: number };
    /** Current Chinese typography, explicitly replacing the omitted foreign title. */
    title: { label: string; opacity: number; rect: { x: number; y: number; width: number; height: number } };
  };
}

export const BATTLE_RESULT_MOVIE_MS = resultAssets.sourceRules.settlementDelayMs;

/** Same GameOverAction ordering; the browser never changes victory or turn state. */
export class BattleResultPresentation {
  private key = '';
  private outcome: BattleResultOutcome = 'draw';
  private settlesAt = 0;
  private startedAt: number | null = null;

  observe(state: GameState, selfId: string, shot: ShotResult | null = state.lastShot): void {
    if (state.phase !== 'finished') { this.reset(); return; }
    const key = `${state.round}:${state.winner}`;
    if (key !== this.key) {
      this.key = key; this.startedAt = null;
      this.outcome = state.winner === 'draw' ? 'draw'
        : state.players.find(player => player.id === selfId)?.team === state.winner ? 'win' : 'loss';
    }
    this.settlesAt = Math.max(shot ? shot.createdAt + shot.duration : 0,
      ...state.players.map(player => player.movementFall ? player.movementFall.startedAt + player.movementFall.duration : 0));
  }

  /** Sample each render tick; moving includes the old player's actions and tombs. */
  sample(serverNow = Date.now(), moving = false): BattleResultFrame | null {
    if (!this.key) return null;
    if (this.startedAt === null && !moving && serverNow >= this.settlesAt) this.startedAt = serverNow;
    const elapsed = this.startedAt === null ? 0 : Math.max(0, serverNow - this.startedAt);
    const stage = this.startedAt === null ? 'waiting' : elapsed < BATTLE_RESULT_MOVIE_MS ? 'movie' : 'settlement';
    const frame: BattleResultFrame = { key: this.key, outcome: this.outcome, stage,
      startedAt: this.startedAt, elapsed, position: resultAssets.sourceRules.position, movie: null };
    // There is no verified ordinary-room tie artwork in the available source.
    if (stage !== 'movie' || this.outcome === 'draw') return frame;
    const movie = resultAssets.assets[this.outcome];
    const { frameIndex, nextFrameIndex, mix } = sourceFrameBlend(elapsed, movie.frameRate, movie.frames.length);
    const rect = movie.titleRects[frameIndex], nextRect = movie.titleRects[nextFrameIndex];
    const lerp = (from: number, to: number) => from + (to - from) * mix;
    frame.movie = { path: movie.path, frameIndex, sourceFrame: movie.rootSourceFrames[frameIndex],
      crop: movie.frameRects[frameIndex], nextCrop: movie.frameRects[nextFrameIndex], mix,
      width: movie.width, height: movie.height, anchor: movie.anchor,
      title: { label: this.outcome === 'win' ? '胜利' : '失败',
        opacity: lerp(movie.titleOpacity[frameIndex], movie.titleOpacity[nextFrameIndex]),
        rect: { x: lerp(rect.x, nextRect.x), y: lerp(rect.y, nextRect.y),
          width: lerp(rect.width, nextRect.width), height: lerp(rect.height, nextRect.height) } } };
    return frame;
  }

  reset(): void { this.key = ''; this.startedAt = null; this.settlesAt = 0; this.outcome = 'draw'; }
}
